import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthGuard } from "@/hooks/use-auth-guard";
import { useToast } from "@/components/ui/use-toast";
import { isPublicRoute } from "@/constants/routes";

interface AuthGuardProps {
  children: ReactNode;
}

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    isAuthenticated,
    isInternalUser,
    isExternalUser,
    isLoading,
    canAccessRoute,
    companyId,
    userSpaces,
  } = useAuthGuard();

  const isPublic = isPublicRoute(location.pathname);

  useEffect(() => {
    if (isPublic) return;

    if (isLoading) return;

    if (!isAuthenticated) {
      return;
    }

    if (isExternalUser) {
      const canAccess = canAccessRoute(location.pathname);

      if (!canAccess) {
        console.warn(
          "🚫 Usuário externo tentando acessar rota não permitida:",
          location.pathname,
          "CompanyId:",
          companyId,
          "UserSpaces:",
          userSpaces,
        );

        // Show specific error message based on route type
        const isExternalRoute = location.pathname.includes("/external/");
        let errorMessage = "Acesso negado";

        if (!isExternalRoute) {
          errorMessage = "Usuários externos só podem acessar rotas /external/";
        } else {
          errorMessage = `Você não tem acesso a este espaço. Espaços disponíveis: ${userSpaces.join(", ") || "nenhum"}`;
        }

        toast({
          variant: "destructive",
          title: "Acesso Negado",
          description: errorMessage,
        });

        navigate("/private-office/external/unauthorized", {
          replace: true,
          state: {
            error: errorMessage,
            attemptedPath: location.pathname,
          },
        });
      }
    }
  }, [
    isPublic,
    isAuthenticated,
    isInternalUser,
    isExternalUser,
    isLoading,
    location.pathname,
    canAccessRoute,
    navigate,
    toast,
    companyId,
    userSpaces,
  ]);

  if (isPublic) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  if (isExternalUser) {
    const canAccess = canAccessRoute(location.pathname);

    if (!canAccess) {
      const isExternalRoute = location.pathname.includes("/external/");

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Acesso Negado 🚫
            </h1>
            {!isExternalRoute ? (
              <div>
                <p className="text-gray-600 mb-4">
                  Usuários externos só podem acessar rotas /external/
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  CompanyId: {companyId}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-gray-600 mb-4">
                  Você não tem acesso a este espaço.
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  Espaços disponíveis:{" "}
                  {userSpaces.length > 0 ? userSpaces.join(", ") : "nenhum"}
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  CompanyId: {companyId}
                </p>
              </div>
            )}
            <p className="text-sm text-gray-500">Redirecionando...</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
